import { Injectable } from "@angular/core";
import { MatSnackBar } from "@angular/material/snack-bar";

import { take } from "rxjs/operators";

const DURATION_DEFAULT = 5000;
const PANEL_CLASS = {
  error: "danger-snackbar",
  warning: "warning-snackbar",
  success: "success-snackbar",
} as const;

export interface AlertPropsGeneric {
  action?: string;
  duration?: number;
  panelClass: keyof typeof PANEL_CLASS;
}

export type AlertProps = Omit<AlertPropsGeneric, "panelClass">;

type Alert = [string, AlertPropsGeneric];

@Injectable({
  providedIn: "root",
})
export class AlertService {
  constructor(private snackBar: MatSnackBar) {}

  public error(message: string, alertProps: AlertProps = {}): Alert {
    return [message, { ...alertProps, panelClass: "error" }];
  }

  public showError(message: string, alertProps: AlertProps = {}) {
    return this.show(...this.error(message, alertProps));
  }

  public warning(message: string, alertProps: AlertProps = {}): Alert {
    return [message, { ...alertProps, panelClass: "warning" }];
  }

  public showWarning(message: string, alertProps: AlertProps = {}) {
    return this.show(...this.warning(message, alertProps));
  }

  public success(message: string, alertProps: AlertProps = {}): Alert {
    return [message, { ...alertProps, panelClass: "success" }];
  }

  public showSuccess(message: string, alertProps: AlertProps = {}) {
    return this.show(...this.success(message, alertProps));
  }

  public showMultiples(alerts: Alert[]) {
    const [currentAlert, ...restAlerts] = alerts;
    if (currentAlert) {
      this.show(...currentAlert)
        .afterDismissed()
        .pipe(take(1))
        .subscribe(() => this.showMultiples(restAlerts))
    }
  }

  private show(
    message: string,
    {
      action = "OK",
      duration = DURATION_DEFAULT,
      panelClass,
    }: AlertPropsGeneric
  ) {
    return this.snackBar.open(message, action, {
      duration,
      panelClass: PANEL_CLASS[panelClass],
    });
  }
}
